/**
 * notificationService — rappels push pour maintenir le streak.
 *
 * Stratégie :
 *  1. Android natif (Capacitor) : LocalNotifications (rappel programmé le
 *     jour même, fonctionne app fermée mais nécessite d'avoir ouvert l'app
 *     ce jour-là) + FCM via PushNotifications (vrai push serveur→app,
 *     fonctionne même si l'app n'a pas été ouverte depuis plusieurs jours —
 *     nécessite un projet Firebase, voir config/firebaseAdmin.js côté backend)
 *  2. Web Push API : service worker reçoit les push du backend (background OK)
 *  3. Fallback : Web Notifications + setTimeout (onglet ouvert uniquement)
 *
 * Flux d'activation :
 *  - registerServiceWorker()  → appelé au montage de l'app
 *  - requestAndSubscribe()    → appelé quand l'utilisateur clique "Activer"
 *  - initNotifications()      → planifie la notif native Capacitor (inchangé)
 */

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import secureAuthService from './secureAuthService';

// ── Constantes ────────────────────────────────────────────────────────────────

const VAPID_PUBLIC_KEY =
  'BJBNCUpiFve7b9LJ1jk2LE6WqJjSuRBIsTLOibLKzNTM7eYhRsZRdzF4Hz6W_npBlTavQARx3obzeV6N9JVPyZU';

const NOTIF_ID_STREAK     = 1;
const NOTIF_SCHEDULED_KEY = 'jogolinga-notif-scheduled-date';
const PERMISSION_ASKED_KEY = 'jogolinga-notif-permission-asked';
const ACTIVITY_HISTORY_KEY = 'jogolinga-activity-history';
const SW_SUBSCRIBED_KEY   = 'jogolinga-push-subscribed';
const REMINDERS_DISABLED_KEY = 'jogolinga-reminders-disabled';
const FCM_TOKEN_KEY = 'jogolinga-fcm-token';
// 'true' quand le backend confirme envoyer lui-même le rappel quotidien
// (FCM sur Android, Web Push sur le site).
// Dans ce cas on ne programme plus de rappel local, sinon l'utilisateur
// reçoit deux notifications presque identiques.
const SERVER_REMINDERS_KEY = 'jogolinga-server-reminders';

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function areRemindersEnabled(): boolean {
  return localStorage.getItem(REMINDERS_DISABLED_KEY) !== 'true';
}

export function hasPracticedToday(): boolean {
  try {
    const raw = localStorage.getItem(ACTIVITY_HISTORY_KEY);
    const history: string[] = raw ? JSON.parse(raw) : [];
    return history.includes(todayISO());
  } catch {
    return false;
  }
}

export function isPermissionGranted(): boolean {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
}

export type NotifStatus = 'granted' | 'denied' | 'default' | 'unsupported';

/**
 * Statut réel de la permission, natif Capacitor en priorité (l'API Web
 * Notification n'existe pas dans la WebView Android).
 */
export async function getNotificationStatus(): Promise<NotifStatus> {
  const LN = getCapacitorLN();
  if (LN) {
    try {
      const perm = await LN.checkPermissions();
      if (perm?.display === 'granted') return 'granted';
      if (perm?.display === 'denied') return 'denied';
      return 'default';
    } catch {
      /* tombe sur le fallback web */
    }
  }
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission as NotifStatus;
}

function getReminderTime(): Date | null {
  const reminder = new Date();
  reminder.setHours(19, 0, 0, 0);
  if (reminder <= new Date()) return null;
  return reminder;
}

function buildMessage(streakCount: number): { title: string; body: string } {
  return {
    title: 'Jogolinga 🌍',
    body:
      streakCount > 0
        ? `🔥 Ne brise pas ta série de ${streakCount} jour${streakCount > 1 ? 's' : ''} !`
        : `Tu n'as pas encore pratiqué aujourd'hui 👋 Reviens apprendre !`,
  };
}

// Convertit la clé VAPID base64url → Uint8Array (requis par PushManager)
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// ── Accès aux plugins Capacitor ───────────────────────────────────────────────
// Depuis Capacitor 3, window.Capacitor.Plugins ne contient un plugin que si son
// module JS a été importé : y lire LocalNotifications sans import renvoyait
// toujours undefined, et Android n'affichait aucune notification.
// Natif uniquement : sur le web on garde le Web Push (service worker).

function getCapacitorLN(): typeof LocalNotifications | null {
  return Capacitor.isNativePlatform() ? LocalNotifications : null;
}

function getCapacitorPN(): typeof PushNotifications | null {
  return Capacitor.isNativePlatform() ? PushNotifications : null;
}

// ── FCM (Firebase Cloud Messaging) ────────────────────────────────────────────
// Push serveur→app sur Android natif : contrairement à LocalNotifications
// (programmée localement, seulement si l'app a été ouverte ce jour-là), le
// backend peut réveiller l'app même après plusieurs jours d'inactivité.

let fcmListenersReady = false;

function ensureFcmListeners(): void {
  const PN = getCapacitorPN();
  if (!PN || fcmListenersReady) return;
  fcmListenersReady = true;

  PN.addListener('registration', (token: { value: string }) => {
    sendFcmTokenToBackend(token.value);
  });
  PN.addListener('registrationError', () => {
    /* silencieux — le rappel local (LocalNotifications) reste actif */
  });
  // Tap sur une notification FCM (app en arrière-plan ou fermée) : l'app
  // relaie l'info via un événement window, écouté par App.tsx.
  PN.addListener('pushNotificationActionPerformed', (action: any) => {
    const data = action?.notification?.data ?? {};
    if (data.type === 'challenge') {
      window.dispatchEvent(new CustomEvent(CHALLENGE_TAP_EVENT, { detail: data }));
    }
  });
}

/** Événement émis quand l'utilisateur tape sur une notification de défi. */
export const CHALLENGE_TAP_EVENT = 'jogolinga:challenge-notification-tap';

/**
 * Android natif uniquement : demande la permission push (dialogue système
 * Android 13+, affiché une seule fois) puis enregistre l'appareil auprès de
 * FCM pour recevoir les notifications de défi, même app fermée.
 * À appeler dès qu'un utilisateur est connecté. Respecte l'opt-out des
 * rappels (le token FCM sert aux deux types de notifications).
 */
export async function enableChallengePush(): Promise<void> {
  const PN = getCapacitorPN();
  if (!PN || !areRemindersEnabled()) return;
  try {
    ensureFcmListeners();
    let perm = await PN.checkPermissions();
    if (perm?.receive === 'prompt' || perm?.receive === 'prompt-with-rationale') {
      perm = await PN.requestPermissions();
    }
    if (perm?.receive === 'granted') await PN.register();
  } catch {
    /* silencieux — le toast in-app reste actif quand l'app est ouverte */
  }
}

async function sendFcmTokenToBackend(token: string): Promise<void> {
  // fetchWithAnyAuth : couvre aussi les comptes Google (session Supabase),
  // pour lesquels secureAuthService n'a pas de token propriétaire.
  try {
    const res = await secureAuthService.fetchWithAnyAuth('/api/push/fcm-token', {
      method: 'POST',
      body: JSON.stringify({ token, platform: 'android' }),
    });
    if (res?.ok) {
      localStorage.setItem(FCM_TOKEN_KEY, token);
      const json = await res.json().catch(() => ({}));
      if (json?.serverReminders === true) {
        localStorage.setItem(SERVER_REMINDERS_KEY, 'true');
        await cancelStreakReminder();
      } else {
        localStorage.removeItem(SERVER_REMINDERS_KEY);
      }
    }
  } catch {
    /* silencieux — la notif locale programmée reste active */
  }
}

async function removeFcmTokenFromBackend(): Promise<void> {
  const token = localStorage.getItem(FCM_TOKEN_KEY);
  if (!token) return;
  try {
    await secureAuthService.fetchWithAnyAuth('/api/push/fcm-token', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    });
  } catch {
    /* silencieux */
  } finally {
    localStorage.removeItem(FCM_TOKEN_KEY);
    localStorage.removeItem(SERVER_REMINDERS_KEY);
  }
}

/**
 * À appeler au logout, AVANT supabase.auth.signOut() (l'appel backend a
 * besoin de la session) : détache le token FCM du compte pour que cet
 * appareil ne reçoive plus ses notifications (défis, rappels).
 */
export async function unregisterPushOnLogout(): Promise<void> {
  await removeFcmTokenFromBackend();
}

// ── Service Worker ────────────────────────────────────────────────────────────

let swRegistration: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js');
  } catch {
    /* silencieux — pas bloquant */
  }
}

// ── Souscription Web Push ─────────────────────────────────────────────────────

async function getOrCreateSubscription(): Promise<PushSubscription | null> {
  if (!('PushManager' in window)) return null;

  try {
    const reg = swRegistration ?? await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    return sub;
  } catch {
    return null;
  }
}

async function sendSubscriptionToBackend(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  try {
    // fetchWithAnyAuth : couvre aussi les comptes Google ; renvoie null pour
    // un invité (pas de backend → seul le rappel local existe).
    const res = await secureAuthService.fetchWithAnyAuth('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      }),
    });
    if (res?.ok) {
      const body = await res.json().catch(() => ({}));
      if (body?.serverReminders === true) {
        localStorage.setItem(SERVER_REMINDERS_KEY, 'true');
        await cancelStreakReminder();
      } else {
        localStorage.removeItem(SERVER_REMINDERS_KEY);
      }
    }
  } catch {
    /* silencieux — la sub locale reste active pour les notifs in-session */
  }
}

/**
 * Site web : renvoie au backend l'abonnement Web Push existant (sans en créer
 * ni redemander la permission). À appeler quand un utilisateur est connecté :
 * rattache l'abonnement au compte et récupère « serverReminders », ce qui
 * coupe le rappel local en double pour les abonnés d'avant ce correctif.
 */
export async function syncWebPush(): Promise<void> {
  if (Capacitor.isNativePlatform() || !areRemindersEnabled()) return;
  if (localStorage.getItem(SW_SUBSCRIBED_KEY) !== 'true') return;
  if (!isPermissionGranted() || !('serviceWorker' in navigator)) return;
  try {
    const reg = swRegistration ?? await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sendSubscriptionToBackend(sub);
  } catch {
    /* silencieux — le rappel local reste actif */
  }
}

// ── API publique ──────────────────────────────────────────────────────────────

function currentStreak(): number {
  try {
    const raw = localStorage.getItem('jogolinga-daily-streak');
    return raw ? (JSON.parse(raw).streak ?? 0) : 0;
  } catch {
    return 0;
  }
}

/**
 * Demande la permission et souscrit au Web Push.
 * Appelé quand l'utilisateur clique "Activer" dans la bannière.
 */
export async function requestAndSubscribe(): Promise<boolean> {
  localStorage.setItem(PERMISSION_ASKED_KEY, 'true');
  localStorage.removeItem(REMINDERS_DISABLED_KEY);

  // Tentative Capacitor (natif)
  const LN = getCapacitorLN();
  if (LN) {
    try {
      const perm = await LN.requestPermissions();
      if (perm?.display === 'granted') {
        localStorage.setItem(SW_SUBSCRIBED_KEY, 'true');
        await scheduleStreakReminder(currentStreak());

        // FCM : push serveur, fonctionne même après plusieurs jours sans ouvrir l'app
        const PN = getCapacitorPN();
        if (PN) {
          try {
            ensureFcmListeners();
            const pnPerm = await PN.requestPermissions();
            if (pnPerm?.receive === 'granted') await PN.register();
          } catch {
            /* silencieux — le rappel local (LocalNotifications) reste actif */
          }
        }

        return true;
      }
    } catch {}
  }

  // Web Notifications API + service worker push
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'denied') return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const sub = await getOrCreateSubscription();
  if (sub) {
    await sendSubscriptionToBackend(sub);
    localStorage.setItem(SW_SUBSCRIBED_KEY, 'true');
  }

  await scheduleStreakReminder(currentStreak());

  return true;
}

/**
 * Compatibilité ascendante — initialise les notifs natives Capacitor.
 * Le web push passe par requestAndSubscribe() déclenché par la bannière.
 */
export async function initNotifications(streakCount: number): Promise<void> {
  if (hasPracticedToday()) return;
  await scheduleStreakReminder(streakCount);
}

// ── Planification native ──────────────────────────────────────────────────────

// ── Préférences par type (rappel, culture, défis) ─────────────────────────────
// Le serveur fait foi (il décide quoi envoyer) ; on garde une copie locale
// pour afficher l'écran tout de suite et pour le rappel local Android.

export type NotifPrefKey = 'reminders' | 'culture' | 'challenges';
export type NotifPrefs = Record<NotifPrefKey, boolean>;

const NOTIF_PREFS_KEY = 'jogolinga-notif-prefs';
const DEFAULT_PREFS: NotifPrefs = { reminders: true, culture: true, challenges: true };

export function getLocalNotifPrefs(): NotifPrefs {
  try {
    const raw = localStorage.getItem(NOTIF_PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : { ...DEFAULT_PREFS };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function setLocalNotifPrefs(prefs: NotifPrefs): void {
  try { localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

/** Récupère les préférences du compte (si connecté) et met à jour la copie locale. */
export async function loadNotifPrefs(): Promise<NotifPrefs> {
  try {
    const res = await secureAuthService.fetchWithAnyAuth('/api/push/preferences');
    if (res?.ok) {
      const prefs: NotifPrefs = { ...DEFAULT_PREFS, ...(await res.json()) };
      setLocalNotifPrefs(prefs);
      return prefs;
    }
  } catch {}
  return getLocalNotifPrefs();
}

/**
 * Change une préférence. Renvoie false si le serveur n'a pas pu l'enregistrer
 * (la copie locale est alors remise comme avant).
 */
export async function saveNotifPref(key: NotifPrefKey, value: boolean): Promise<boolean> {
  const previous = getLocalNotifPrefs();
  setLocalNotifPrefs({ ...previous, [key]: value });
  if (key === 'reminders') {
    if (value) {
      localStorage.removeItem(NOTIF_SCHEDULED_KEY);
      scheduleStreakReminder(currentStreak());
    } else {
      cancelStreakReminder();
    }
  }
  try {
    const res = await secureAuthService.fetchWithAnyAuth('/api/push/preferences', {
      method: 'PUT',
      body: JSON.stringify({ [key]: value }),
    });
    // Pas connecté (res null) : seul le rappel local existe, la copie locale suffit
    if (res === null || res.ok) return true;
  } catch {}
  setLocalNotifPrefs(previous);
  return false;
}

// Rappel web de secours (onglet ouvert) : gardé pour pouvoir l'annuler si le
// serveur prend le relais en cours de session.
let webReminderTimer: ReturnType<typeof setTimeout> | null = null;

export async function scheduleStreakReminder(streakCount: number): Promise<void> {
  if (!areRemindersEnabled()) return;
  if (!getLocalNotifPrefs().reminders) return;
  if (hasPracticedToday()) return;

  const today = todayISO();
  if (localStorage.getItem(NOTIF_SCHEDULED_KEY) === today) return;

  const reminderTime = getReminderTime();
  if (!reminderTime) return;

  const { title, body } = buildMessage(streakCount);

  if (localStorage.getItem(SERVER_REMINDERS_KEY) === 'true') {
    // Le serveur envoie déjà le rappel (et seulement si pas pratiqué) :
    // pas de rappel local en plus, sur Android comme sur le site.
    await cancelStreakReminder();
    return;
  }

  // Capacitor LocalNotifications
  const LN = getCapacitorLN();
  if (LN) {
    try {
      const perm = await LN.checkPermissions();
      if (perm?.display === 'granted') {
        await LN.schedule({
          notifications: [
            {
              id: NOTIF_ID_STREAK,
              title,
              body,
              // Alarme non exacte (pas de permission SCHEDULE_EXACT_ALARM) ;
              // allowWhileIdle la laisse quand même sonner en veille (Doze).
              schedule: { at: reminderTime, allowWhileIdle: true },
              smallIcon: 'ic_stat_icon_config_sample',
              iconColor: '#C87040',
              extra: { type: 'streak_reminder' },
            },
          ],
        });
        localStorage.setItem(NOTIF_SCHEDULED_KEY, today);
        return;
      }
    } catch {}
  }

  // Fallback : setTimeout (onglet ouvert uniquement)
  if (!isPermissionGranted()) return;

  const delay = reminderTime.getTime() - Date.now();
  if (webReminderTimer) clearTimeout(webReminderTimer);
  webReminderTimer = setTimeout(() => {
    webReminderTimer = null;
    if (hasPracticedToday()) return;
    try {
      new Notification(title, {
        body,
        icon: '/assets/web-app-manifest-192x192.png',
        badge: '/assets/favicon-96x96.png',
        tag: 'jogolinga-streak',
      });
    } catch {}
  }, delay);

  localStorage.setItem(NOTIF_SCHEDULED_KEY, today);
}

export async function cancelStreakReminder(): Promise<void> {
  if (webReminderTimer) {
    clearTimeout(webReminderTimer);
    webReminderTimer = null;
  }
  const LN = getCapacitorLN();
  if (LN) {
    try {
      await LN.cancel({ notifications: [{ id: NOTIF_ID_STREAK }] });
    } catch {}
  }
}

/**
 * Opt-out applicatif : la permission OS reste accordée (ni le navigateur ni
 * Android ne permettent de la révoquer depuis l'app), mais on annule le
 * rappel programmé, on se désabonne du push serveur, et on empêche toute
 * reprogrammation tant que l'utilisateur n'a pas cliqué "Réactiver".
 */
export async function disableReminders(): Promise<void> {
  localStorage.setItem(REMINDERS_DISABLED_KEY, 'true');
  await cancelStreakReminder();
  localStorage.removeItem(NOTIF_SCHEDULED_KEY);
  await removeFcmTokenFromBackend();

  try {
    if ('serviceWorker' in navigator) {
      const reg = swRegistration ?? await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        if (secureAuthService.isAuthenticated()) {
          try {
            await secureAuthService.authenticatedFetch('/api/push/subscribe', {
              method: 'DELETE',
              body: JSON.stringify({ endpoint: sub.endpoint }),
            });
          } catch {}
        }
        await sub.unsubscribe();
      }
    }
  } catch {}

  localStorage.removeItem(SW_SUBSCRIBED_KEY);
  localStorage.removeItem(SERVER_REMINDERS_KEY);
}

export async function requestNotificationPermission(): Promise<boolean> {
  return requestAndSubscribe();
}
